import {
  Button,
  ButtonIcon,
  ButtonSpinner,
  ButtonText,
} from "@repo/components/src/ui/button";
import { Box } from "@repo/components/src/ui/box";
import { HStack } from "@repo/components/src/ui/hstack";
import { Input, InputField } from "@repo/components/src/ui/input";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import MontuEmblem from "@repo/components/src/shared/agent/MontuEmblem";
import { ArrowRight } from "lucide-react-native";
import React, { useCallback, useEffect, useRef, useState } from "react";
import type { ComponentRef } from "react";
import type { TextInputInstance, TextInputKeyPressEvent } from "react-native";
import SuggestionChips from "./SuggestionChips";
import type { SuggestionChip } from "./suggestionChipsData";
import { NEW_CAMPAIGN_CHIPS } from "./suggestionChipsData";

type Props = {
  /** "panel" — 1a's rounded card, tucked in the padded content area.
   * "hero" — 1b's larger, deeper lead-magnet hero. */
  variant: "panel" | "hero";
  badgeLabel: string;
  title: React.ReactNode;
  subtitle: string;
  placeholder: string;
  ctaLabel: string;
  chipsLabel: string;
  /** Which suggestion chips this surface shows. Defaults to the 1a roster. */
  chips?: SuggestionChip[];
  onSubmit: (text: string) => void;
  isSubmitting?: boolean;
  /** Absolutely-positioned floating illustrations, rendered behind the content. */
  decorations?: React.ReactNode;
  /** Extra rows pinned to the bottom of the panel — 1b's consolidated
   * social-proof/call row and scroll cue. */
  children?: React.ReactNode;
  /** Extra classes for the outer panel (e.g. 1b's full-viewport min-height). */
  className?: string;
};

// React Native's `key-press` event only declares `key`, but the real DOM event on
// web carries modifiers too — read defensively rather than asserting a type that
// does not hold on native.
const isShiftPressed = (nativeEvent: object): boolean =>
  "shiftKey" in nativeEvent && nativeEvent.shiftKey === true;

/**
 * The dark gradient AI composer hero shared by both "new campaign" screens.
 * `variant` only changes chrome (padding, radius, type scale) — the
 * behavior (submit, chip prefill) is identical, so it lives here once
 * instead of being duplicated between the 1a panel and the 1b hero.
 */
const ComposerPanel = React.memo(
  ({
    variant,
    badgeLabel,
    title,
    subtitle,
    placeholder,
    ctaLabel,
    chipsLabel,
    chips = NEW_CAMPAIGN_CHIPS,
    onSubmit,
    isSubmitting = false,
    decorations,
    children,
    className,
  }: Props) => {
    const [text, setText] = useState("");
    const [shaking, setShaking] = useState(false);
    const inputRef = useRef<ComponentRef<typeof InputField>>(null);
    const isHero = variant === "hero";

    // The CTA is never grayed out: submitting with an empty input focuses the
    // field and nudges it (brand border + ~4px shake) instead of disabling.
    const handleSubmit = useCallback(() => {
      if (isSubmitting) return;
      const trimmed = text.trim();
      if (trimmed.length === 0) {
        // `InputField`'s generated ref type is `TextInputType` (a
        // gluestack-ui/core typing gap) — cast narrowly for `.focus()`.
        (inputRef.current as unknown as TextInputInstance | null)?.focus();
        setShaking(true);
        return;
      }
      onSubmit(trimmed);
    }, [text, isSubmitting, onSubmit]);

    useEffect(() => {
      if (!shaking) return;
      const timeout = setTimeout(() => setShaking(false), 350);
      return () => clearTimeout(timeout);
    }, [shaking]);

    const handleKeyPress = useCallback(
      (event: TextInputKeyPressEvent) => {
        if (event.nativeEvent.key !== "Enter") return;
        if (isShiftPressed(event.nativeEvent)) return;
        event.preventDefault();
        handleSubmit();
      },
      [handleSubmit],
    );

    // Chips are actions, not prefills: picking one starts the AI flow with
    // the chip's prompt as the opening message.
    const handlePick = useCallback(
      (prompt: string) => {
        if (isSubmitting) return;
        onSubmit(prompt);
      },
      [isSubmitting, onSubmit],
    );

    return (
      <VStack
        className={`relative w-full overflow-hidden rounded-[26px] px-11 pt-[38px] ${
          isHero ? "bg-hero-lead pb-6" : "bg-hero-panel pb-[34px]"
        } ${className ?? ""}`}
      >
        {decorations}
        <VStack
          space={isHero ? "lg" : "md"}
          className={`relative z-10 w-full ${isHero ? "flex-1" : ""}`}
        >
          <HStack
            space="sm"
            className="items-center self-start rounded-full border border-white/20 bg-white/10 px-3.5 py-1.5"
          >
            <MontuEmblem size={16} />
            <Text className="text-xs font-extrabold uppercase tracking-widest text-typography-white">
              {badgeLabel}
            </Text>
          </HStack>

          <VStack space="sm" className="max-w-[660px]">
            <Text
              bold
              className="text-5xl leading-none tracking-tight text-typography-white"
            >
              {title}
            </Text>
            <Text className="max-w-[520px] text-lg leading-normal text-brand-50">
              {subtitle}
            </Text>
          </VStack>

          <HStack
            space="sm"
            className={`max-w-[900px] items-center rounded-[18px] border-2 bg-background-0 py-2 pl-5 pr-2 shadow-composer ${
              shaking
                ? "animate-input-shake border-brand-400"
                : "border-transparent"
            }`}
          >
            <Box className="shrink-0">
              <MontuEmblem size={24} thinking />
            </Box>
            <Input
              variant="outline"
              size="md"
              className="flex-1 border-0 bg-transparent data-[focus=true]:border-0"
            >
              <InputField
                ref={inputRef}
                placeholder={placeholder}
                value={text}
                onChangeText={setText}
                onKeyPress={handleKeyPress}
                accessibilityLabel={placeholder}
                returnKeyType="send"
                className="text-base"
              />
            </Input>
            <Button
              action="brand"
              variant="solid"
              size="md"
              rounded="xl"
              isDisabled={isSubmitting}
              onPress={handleSubmit}
            >
              {isSubmitting ? <ButtonSpinner /> : null}
              <ButtonText>{ctaLabel}</ButtonText>
              {!isSubmitting ? <ButtonIcon as={ArrowRight} /> : null}
            </Button>
          </HStack>

          <SuggestionChips
            label={chipsLabel}
            chips={chips}
            onPick={handlePick}
          />

          {/* `mt-auto` pins whatever the caller appends (1b's bottom row +
           * scroll cue) to the panel's bottom edge when the panel is taller
           * than its content — inert when it isn't. */}
          {children ? <Box className="mt-auto">{children}</Box> : null}
        </VStack>
      </VStack>
    );
  },
);

ComposerPanel.displayName = "ComposerPanel";

export default ComposerPanel;
